import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowsClockwise,
  ArrowSquareOut,
  TrendUp,
  ShieldCheck,
  Target,
  Brain,
  Clock,
  Lock,
  Warning,
  CaretDown,
  CaretUp,
  Star,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tradingViewStockUrl, screenerStockUrl } from "../../lib/tradingview";
import { useAuth } from "../../contexts/AuthContext";
import AdminEditTradePlan from "../AdminEditTradePlan";
import StockList from "./StockList";

const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

const ratingColor = {
  Strong: "text-pos border-pos bg-pos-soft",
  Moderate: "text-mod border-mod bg-mod-soft",
  Weak: "text-neg border-neg bg-neg-soft",
};

const scoreColor = (s) => {
  if (s == null) return "text-muted-fg border-subtle";
  if (s >= 8) return "text-pos border-pos bg-pos-soft";
  if (s >= 6) return "text-mod border-mod bg-mod-soft";
  return "text-neg border-neg bg-neg-soft";
};

function RetestCard({ item, onWatch, watched, onItemUpdated }) {
  const [open, setOpen] = useState(false);
  const [adjusted, setAdjusted] = useState(true); // default: retest-adjusted plan
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const sym = (item.symbol || "").replace(".NS", "");
  const distFromEntry = item.entry
    ? ((item.ltp - item.entry) / item.entry) * 100
    : 0;
  const observed = item.first_observed_at
    ? new Date(item.first_observed_at).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })
    : "—";

  // Compute retest-adjusted trade plan.
  // - Entry: current LTP (cheaper, no need to wait)
  // - SL: weekly retest candle's low (the actual support buyers defended).
  //   Fallback: tighter of (original_entry × 0.98) or (LTP × 0.96).
  // - Target: unchanged (full original upside)
  const adjEntry = item.ltp;
  const adjSl =
    item.week_low && item.week_low > 0
      ? item.week_low
      : Math.max((item.entry ?? item.ltp) * 0.98, (item.ltp ?? 0) * 0.96);
  const adjTarget = item.target;
  const adjRR =
    adjEntry > adjSl && adjTarget > adjEntry
      ? (adjTarget - adjEntry) / (adjEntry - adjSl)
      : null;
  const origRR =
    item.entry > item.stop_loss && item.target > item.entry
      ? (item.target - item.entry) / (item.entry - item.stop_loss)
      : null;

  const plan = adjusted
    ? { entry: adjEntry, sl: adjSl, target: adjTarget, rr: adjRR }
    : { entry: item.entry, sl: item.stop_loss, target: item.target, rr: origRR };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="border border-subtle bg-surface rounded-sm p-5 hover:border-pos/40 transition"
      data-testid={`retest-card-${sym}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <a
              href={tradingViewStockUrl(item.symbol)}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-lg font-bold text-primary-fg hover:text-pos flex items-center gap-1"
              data-testid={`retest-tv-${sym}`}
            >
              {sym} <ArrowSquareOut size={12} weight="bold" />
            </a>
            <span
              className={
                "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                scoreColor(item.score)
              }
              data-testid={`retest-score-${sym}`}
              title={`Quality score: ${item.score}/10 · ${item.score_tier || ""}`}
            >
              {item.score}/10
            </span>
            {item.fundamental_rating && (
              <span
                className={
                  "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                  (ratingColor[item.fundamental_rating] || "text-muted-fg border-subtle")
                }
                data-testid={`retest-fundamental-${sym}`}
                title={`Fundamental: ${item.fundamental_rating}${item.fundamental_score ? " (" + item.fundamental_score + "/10)" : ""}`}
              >
                <Brain size={9} weight="fill" className="inline mr-0.5" />
                {item.fundamental_rating}
              </span>
            )}
          </div>
          <div className="text-sm text-muted-fg">
            {item.company_name || sym}
          </div>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          {onWatch && (
            <button
              type="button"
              onClick={() => onWatch({ symbol: item.symbol })}
              data-testid={`retest-watch-${sym}`}
              title={watched ? "Remove from watchlist" : "Add to watchlist"}
              className={
                "p-1.5 rounded-sm border transition " +
                (watched
                  ? "bg-pos-soft border-pos text-pos hover:bg-pos-soft"
                  : "border-subtle text-muted-fg hover:text-pos hover:border-pos/40")
              }
            >
              <Star size={13} weight={watched ? "fill" : "regular"} />
            </button>
          )}
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
              Current
            </div>
            <div className="font-mono text-lg text-primary-fg tabular-nums">
              ₹{fmt(item.ltp)}
            </div>
            {typeof item.pct_change_today === "number" && (
              <div
                className={
                  "text-[10px] font-mono tabular-nums " +
                  (item.pct_change_today >= 0 ? "text-pos" : "text-neg")
                }
                title={
                  item.last_price_refreshed_at
                    ? `Today's change · refreshed ${new Date(item.last_price_refreshed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                    : "Today's change vs prev close"
                }
              >
                {item.pct_change_today >= 0 ? "+" : ""}
                {item.pct_change_today.toFixed(2)}% today
              </div>
            )}
            <div
              className={
                "text-[10px] font-mono tabular-nums " +
                (Math.abs(distFromEntry) < 1
                  ? "text-pos"
                  : distFromEntry > 0
                  ? "text-mod"
                  : "text-neg")
              }
            >
              Entry ₹{fmt(item.entry)}
              {" · "}
              {distFromEntry >= 0 ? "+" : ""}
              {fmt(distFromEntry)}%
            </div>
          </div>
        </div>
      </div>

      {/* Plan toggle */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-1.5">
          Trade plan
          {plan.rr && (
            <span className="text-secondary-fg">
              · R:R 1:{plan.rr.toFixed(1)}
            </span>
          )}
        </div>
        <div
          className="inline-flex border border-subtle rounded-sm p-0.5 bg-app"
          data-testid={`retest-plan-toggle-${sym}`}
        >
          <button
            type="button"
            onClick={() => setAdjusted(true)}
            data-testid={`retest-plan-adjusted-${sym}`}
            className={
              "px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest rounded-sm transition " +
              (adjusted ? "bg-pos-soft text-pos" : "text-muted-fg hover:text-primary-fg")
            }
          >
            Retest
          </button>
          <button
            type="button"
            onClick={() => setAdjusted(false)}
            data-testid={`retest-plan-original-${sym}`}
            className={
              "px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest rounded-sm transition " +
              (!adjusted ? "bg-surface-hover text-primary-fg" : "text-muted-fg hover:text-primary-fg")
            }
          >
            Original
          </button>
        </div>
      </div>

      {/* Trade plan */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
            <TrendUp size={9} weight="bold" /> Entry
          </div>
          <div className="text-primary-fg font-mono text-sm tabular-nums">
            ₹{fmt(plan.entry)}
          </div>
        </div>
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-neg mb-1">
            <ShieldCheck size={9} weight="bold" /> SL
          </div>
          <div className="text-neg font-mono text-sm tabular-nums">
            ₹{fmt(plan.sl)}
          </div>
          {adjusted && item.week_low && plan.sl === item.week_low && (
            <div className="text-[8px] font-mono uppercase tracking-widest text-muted-fg mt-0.5">
              Weekly low
            </div>
          )}
        </div>
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
            <Target size={9} weight="bold" /> Exit
          </div>
          <div className="text-pos font-mono text-sm tabular-nums">
            ₹{fmt(plan.target)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        <span>
          Original breakout: <span className="text-secondary-fg">{item.breakout_date || "—"}</span>
        </span>
        <span>
          Retest spotted: <span className="text-pos">{observed}</span>
        </span>
      </div>

      {/* Deep links */}
      <div className="flex items-center gap-2 mb-3">
        <a
          href={tradingViewStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`retest-tv-btn-${sym}`}
        >
          TV ↗
        </a>
        <a
          href={screenerStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`retest-scr-${sym}`}
        >
          SCR ↗
        </a>
      </div>

      {/* Admin-only Edit Trade Plan panel — operates on the underlying
          original signal (not the retest-adjusted view). */}
      {isAdmin && item.signal_type && item.first_detected_at && (
        <div className="mb-3">
          <AdminEditTradePlan
            signal={{
              entry: item.entry,
              target: item.target,
              stop_loss: item.stop_loss,
              symbol: item.symbol,
              manually_edited: item.manually_edited,
              manually_edited_by: item.manually_edited_by,
              manually_edited_at: item.manually_edited_at,
            }}
            endpoint="/admin/signals/trade-plan"
            identifier={{
              symbol: item.symbol,
              signal_type: item.signal_type,
              first_detected_at: item.first_detected_at,
            }}
            testIdSuffix={`retest-${sym}`}
            onSaved={(updated) => onItemUpdated?.(updated)}
          />
        </div>
      )}

      {/* Fundamental analysis (collapsible) */}
      {item.fundamental_summary ? (
        <div className="mt-4 pt-3 border-t border-subtle">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`retest-fund-toggle-${sym}`}
            className="flex items-center justify-between w-full text-left text-xs hover:text-primary-fg"
          >
            <span className="font-mono uppercase tracking-widest text-[10px] text-secondary-fg flex items-center gap-1">
              <Brain size={11} weight="bold" className="text-pos" /> Fundamentals (AI)
            </span>
            {open ? <CaretUp size={11} /> : <CaretDown size={11} />}
          </button>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.2 }}
              className="mt-2 space-y-2"
            >
              <p className="text-xs text-secondary-fg leading-relaxed">
                {item.fundamental_summary}
              </p>
              {item.fundamental_bullish?.length > 0 && (
                <ul className="space-y-1">
                  {item.fundamental_bullish.slice(0, 3).map((b, i) => (
                    <li key={`bull-${i}-${b.slice(0, 24)}`} className="text-[11px] text-secondary-fg flex gap-1.5">
                      <span className="text-pos shrink-0">+</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {item.fundamental_bearish?.length > 0 && (
                <ul className="space-y-1">
                  {item.fundamental_bearish.slice(0, 2).map((b, i) => (
                    <li key={`bear-${i}-${b.slice(0, 24)}`} className="text-[11px] text-secondary-fg flex gap-1.5">
                      <span className="text-neg shrink-0">−</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-subtle">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            <Warning size={11} className="text-mod" /> Fundamentals analysis pending
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function RetestView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/retests")
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], is_premium_view: true }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center text-muted-fg py-16" data-testid="retest-loading">
        Loading retest setups…
      </div>
    );
  }

  const items = data?.items || [];
  const isPremium = data?.is_premium_view;

  return (
    <div data-testid="retest-view">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ArrowsClockwise size={14} weight="bold" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Re-Test setups
            </div>
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-pos/40 text-pos bg-pos-soft rounded-sm">
              Last 7 days
            </span>
          </div>
          <p className="text-xs text-muted-fg max-w-2xl leading-relaxed">
            Multi-year breakouts that ran up and have now pulled back to the breakout level —
            classic high-probability re-entry. Click symbol to open on TradingView.
          </p>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-2">
          <Clock size={11} weight="bold" />
          {items.length} setup{items.length === 1 ? "" : "s"} active
        </div>
      </div>

      {items.length === 0 ? (
        <div
          className="p-10 border border-subtle rounded-sm bg-surface text-center"
          data-testid="retest-empty"
        >
          <ArrowsClockwise size={24} className="mx-auto text-muted-fg mb-3" />
          <div className="text-primary-fg font-medium mb-1">No retest setups right now</div>
          <div className="text-sm text-muted-fg max-w-md mx-auto">
            We need a multi-year breakout to first run up &gt;5% and then pull back to the breakout
            level. The detector refreshes every 30 minutes during market hours.
          </div>
        </div>
      ) : (
        <>
          <StockList
            items={items}
            onOpen={(it) => onOpenStock?.({ ...it, retest_mode: true })}
            onWatch={onWatch}
            watchedSet={watchedSet}
            testIdPrefix="retest-row"
            testid="retest-list"
            rowProps={() => ({
              iconKind: "retest",
              typeBadge: { label: "Retest", tone: "mod" },
            })}
          />
          {!isPremium && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
              data-testid="retest-upgrade"
            >
              <div className="flex items-center gap-3">
                <Lock size={20} className="text-pos shrink-0" />
                <div>
                  <div className="font-medium text-primary-fg">
                    Showing 1 of all retest setups
                  </div>
                  <div className="text-sm text-secondary-fg">
                    Unlock all current retest opportunities with a ₹5 trial.
                  </div>
                </div>
              </div>
              <Link
                to="/pricing"
                className="px-5 py-2.5 rounded-sm text-sm font-medium"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                Start ₹5 trial →
              </Link>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
